import GIF from 'gif.js'

import type { GifEncoderAdapter, ExportProgressCallback } from './GifEncoderAdapter'
import type { NormalizedGifExportConfig } from '../types'

function mapDitherMode(mode: NormalizedGifExportConfig['dither']): string | boolean {
  switch (mode) {
    case 'none':
      return false
    case 'ordered':
      return 'Bayer'
    case 'floyd-steinberg':
    default:
      return 'FloydSteinberg'
  }
}

export class GifJsEncoder implements GifEncoderAdapter {
  private gif: GIF | null = null
  private finishPromise: Promise<Blob> | null = null
  private resolveFinish?: (blob: Blob) => void
  private rejectFinish?: (error: Error) => void

  constructor(private readonly onProgress?: ExportProgressCallback) {}

  async start(config: NormalizedGifExportConfig): Promise<void> {
    this.finishPromise = new Promise<Blob>((resolve, reject) => {
      this.resolveFinish = resolve
      this.rejectFinish = reject
    })

    this.gif = new GIF({
      workers: 2,
      quality: config.quality,
      width: config.width,
      height: config.height,
      workerScript: `${import.meta.env.BASE_URL}gif.worker.js`,
      repeat: config.loop,
      dither: mapDitherMode(config.dither),
    })

    this.gif.on('progress', (progress: number) => {
      this.onProgress?.({
        status: 'encoding',
        progress: Math.round(80 + progress * 20),
        currentFrame: config.totalFrames,
        totalFrames: config.totalFrames,
        message: `Encoding GIF ${Math.round(progress * 100)}%`,
      })
    })

    this.gif.on('finished', (blob: Blob) => {
      this.resolveFinish?.(blob)
      this.clearPromiseHooks()
    })

    this.gif.on('abort', () => {
      this.rejectFinish?.(new Error('GIF export aborted'))
      this.clearPromiseHooks()
    })
  }

  async addFrame(canvas: HTMLCanvasElement, delayMs: number): Promise<void> {
    if (!this.gif) {
      throw new Error('GIF encoder has not been started')
    }

    this.gif.addFrame(canvas, {
      delay: delayMs,
      copy: true,
    })
  }

  async finish(): Promise<Blob> {
    if (!this.gif || !this.finishPromise) {
      throw new Error('GIF encoder has not been started')
    }

    this.gif.render()
    return this.finishPromise
  }

  async cancel(): Promise<void> {
    if (this.gif) {
      this.gif.abort()
      this.gif = null
    }
  }

  private clearPromiseHooks(): void {
    this.resolveFinish = undefined
    this.rejectFinish = undefined
    this.gif = null
  }
}
